import { FormikValues } from "formik";
import React, { ReactNode, Suspense, useCallback, useMemo } from "react";
import {
  FieldVisibilityCallback,
  useFormFieldVisibility,
} from "../../hooks/useFormFieldVisibility";
import { FormConfig, FormConfigDividerItem, FormConfigItem, FormItemType } from "../../types/form";
import { FormFieldItem } from "../FormFieldItem/FormFieldItem";
import { FormHTMLBlockItem } from "../FormHTMLBlockItem/FormHTMLBlockItem";
import { FormSectionItem } from "../FormSectionItem/FormSectionItem";
import { FormImageItem } from "../FormImageItem/FormImageItem";
import { FormVideoItem } from "../FormVideoItem/FormVideoItem";
import { FormCalloutItem } from "../FormCalloutItem/FormCalloutItem";
import { FormDividerItem } from "../FormDividerItem/FormDividerItem";
import { FormRecaptchaItem } from "../FormRecaptchaItem/FormRecaptchaItem";
import { FormConsentItem } from "../DisplayElements/FormConsentItem/FormConsentItem";
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";

// Lazy-loaded so pdf.js ships in its own chunk instead of the main bundle —
// most forms contain no PDF block.
const FormPdfViewerItem = React.lazy(() =>
  import(
    /* webpackChunkName: "pdf-viewer" */ "../DisplayElements/FormPdfViewerItem/FormPdfViewerItem"
  ).then((m) => ({ default: m.FormPdfViewerItem }))
);

export interface FormItemsProps {
  config: FormConfig;
  formItems: FormConfigItem[];
  formValues: FormikValues;
  // Items used for visibility context; defaults to formItems. Used by multi-page forms
  // so cross-page condition chains resolve correctly while only the current page renders.
  visibilityItems?: FormConfigItem[];
}

// Memoized render functions to prevent unnecessary re-renders
const MemoizedFormFieldItem = React.memo(FormFieldItem);
const MemoizedFormHTMLBlockItem = React.memo(FormHTMLBlockItem);
const MemoizedFormImageItem = React.memo(FormImageItem);
const MemoizedFormVideoItem = React.memo(FormVideoItem);
const MemoizedFormCalloutItem = React.memo(FormCalloutItem);
const MemoizedFormDividerItem = React.memo(FormDividerItem);
const MemoizedFormRecaptchaItem = React.memo(FormRecaptchaItem);
const MemoizedFormConsentItem = React.memo(FormConsentItem);
const MemoizedFormSectionItem = React.memo(FormSectionItem);

export const FormItems: React.FC<FormItemsProps> = ({
  config,
  formItems,
  formValues,
  visibilityItems,
}) => {
  const appFieldMap = useMemo(() => {
    return new Map(config?.application?.structure?.map((appField) => [appField.slug, appField]));
  }, [config?.application?.structure]);

  const { checkFieldVisibility } = useFormFieldVisibility(
    visibilityItems ?? formItems,
    formValues,
    appFieldMap
  );

  const renderFormFieldItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback) => {
      const appField = appFieldMap.get(item.slug);

      return (
        <MemoizedFormFieldItem
          appField={appField}
          item={item}
          key={item.slug}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    [appFieldMap]
  );

  const renderFormHTMLBlockItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormHTMLBlockItem
          key={item.slug}
          state={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderFormHeadingItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormHTMLBlockItem
          key={item.slug}
          state={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderImageItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormImageItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderVideoItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormVideoItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderCalloutItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormCalloutItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderFormDividerItem = useCallback(
    (item: FormConfigDividerItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormDividerItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderRecaptchaItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormRecaptchaItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderFormConsentItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <MemoizedFormConsentItem
          key={item.slug}
          item={item}
          checkFieldVisibility={fieldVisibilityCallback}
        />
      );
    },
    []
  );

  const renderFormPdfViewerItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <ErrorBoundary key={item.slug}>
          <Suspense fallback={null}>
            <FormPdfViewerItem item={item} checkFieldVisibility={fieldVisibilityCallback} />
          </Suspense>
        </ErrorBoundary>
      );
    },
    []
  );

  const renderFormSectionItem = useCallback(
    (item: FormConfigItem, fieldVisibilityCallback: FieldVisibilityCallback): ReactNode => {
      return (
        <ErrorBoundary key={item.slug}>
          <MemoizedFormSectionItem
            item={item}
            checkFieldVisibility={fieldVisibilityCallback}
            renderFormFieldItem={renderFormFieldItem}
            renderFormHeadingItem={renderFormHeadingItem}
            renderFormHTMLBlockItem={renderFormHTMLBlockItem}
            renderFormImageItem={renderImageItem}
            renderFormVideoItem={renderVideoItem}
            renderFormCalloutItem={renderCalloutItem}
            renderFormDividerItem={renderFormDividerItem}
            renderFormRecaptchaItem={renderRecaptchaItem}
            renderFormConsentItem={renderFormConsentItem}
            renderFormPdfViewerItem={renderFormPdfViewerItem}
          />
        </ErrorBoundary>
      );
    },
    [
      renderFormFieldItem,
      renderFormHTMLBlockItem,
      renderImageItem,
      renderVideoItem,
      renderCalloutItem,
      renderFormDividerItem,
      renderRecaptchaItem,
      renderFormConsentItem,
      renderFormPdfViewerItem,
    ]
  );

  // Memoize the render function map to prevent recreation on every render
  const renderFunctionMap = useMemo(
    () => ({
      [FormItemType.field]: renderFormFieldItem,
      [FormItemType.heading]: renderFormHeadingItem,
      [FormItemType.html_block]: renderFormHTMLBlockItem,
      [FormItemType.image]: renderImageItem,
      [FormItemType.video]: renderVideoItem,
      [FormItemType.callout]: renderCalloutItem,
      [FormItemType.divider]: renderFormDividerItem,
      [FormItemType.recaptcha]: renderRecaptchaItem,
      [FormItemType.consent]: renderFormConsentItem,
      [FormItemType.pdf_viewer]: renderFormPdfViewerItem,
      [FormItemType.section]: renderFormSectionItem,
    }),
    [
      renderFormFieldItem,
      renderFormHeadingItem,
      renderFormHTMLBlockItem,
      renderImageItem,
      renderVideoItem,
      renderCalloutItem,
      renderFormDividerItem,
      renderRecaptchaItem,
      renderFormConsentItem,
      renderFormPdfViewerItem,
      renderFormSectionItem,
    ]
  );

  // Memoize the rendered items to prevent unnecessary re-renders
  const renderedItems = useMemo(() => {
    return formItems?.map((item) => {
      const renderFunction = renderFunctionMap[item.type];
      if (renderFunction) {
        return renderFunction(item, checkFieldVisibility);
      }
      return null;
    });
  }, [formItems, renderFunctionMap, checkFieldVisibility]);

  return <>{renderedItems}</>;
};
